import React from 'react';
import { Shield, Lock, Bell, CreditCard, User, Database } from 'lucide-react';
import '../styles/LegalPages.css';

const PrivacyPage = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-updated">Last Updated: December 2025</p>

        <div className="legal-intro">
          <p>
            Market Sentinel AI ("we," "our," or "us") is committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you use our mobile application and services.
          </p>
        </div>

        <section className="legal-section">
          <div className="legal-section-header">
            <User size={24} />
            <h2>Information We Collect</h2>
          </div>
          <div className="legal-content">
            <h3>Account Information</h3>
            <p>
              When you create an account, we collect:
            </p>
            <ul>
              <li>Email address</li>
              <li>Username and password (encrypted)</li>
              <li>Profile preferences and settings</li>
              <li>Device information and identifiers</li>
            </ul>

            <h3>Usage Data</h3>
            <p>
              We automatically collect information about how you interact with our services:
            </p>
            <ul>
              <li>App usage patterns and features accessed</li>
              <li>Market watchlist and preferences</li>
              <li>Alert settings and notification history</li>
              <li>Session duration and frequency</li>
              <li>Device type, operating system, and app version</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <Lock size={24} />
            <h2>Authentication and Security</h2>
          </div>
          <div className="legal-content">
            <p>
              We use industry-standard security measures to protect your account:
            </p>
            <ul>
              <li>Encrypted password storage using bcrypt hashing</li>
              <li>Secure authentication tokens (JWT)</li>
              <li>HTTPS encryption for all data transmission</li>
              <li>Regular security audits and updates</li>
              <li>Two-factor authentication options (where available)</li>
            </ul>
            <p>
              Your password is never stored in plain text and cannot be retrieved by our staff. 
              If you forget your password, we provide a secure reset process.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <Database size={24} />
            <h2>How We Use Your Information</h2>
          </div>
          <div className="legal-content">
            <p>We use collected information for the following purposes:</p>
            <ul>
              <li><strong>Service Delivery:</strong> Provide market analysis, alerts, and trading insights</li>
              <li><strong>Personalization:</strong> Customize content based on your preferences and watchlists</li>
              <li><strong>Communication:</strong> Send important updates, notifications, and support responses</li>
              <li><strong>Analytics:</strong> Improve app performance and user experience</li>
              <li><strong>Security:</strong> Detect and prevent fraudulent activity</li>
              <li><strong>Compliance:</strong> Meet legal and regulatory requirements</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <Bell size={24} />
            <h2>Push Notifications</h2>
          </div>
          <div className="legal-content">
            <p>
              Market Sentinel AI uses push notifications to deliver time-sensitive trading alerts and market updates.
            </p>
            <h3>What We Send</h3>
            <ul>
              <li>Watchtower alerts when opportunities are detected</li>
              <li>Market narrative updates</li>
              <li>Important system announcements</li>
              <li>Subscription and account notifications</li>
            </ul>
            <h3>Your Control</h3>
            <p>
              You can manage notification preferences within the app settings or through your device settings. 
              Disabling notifications may limit the real-time value of our service.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <CreditCard size={24} />
            <h2>Subscription Payments</h2>
          </div>
          <div className="legal-content">
            <p>
              All subscription payments are processed through official app store payment systems 
              (Google Play Store and Apple App Store).
            </p>
            <ul>
              <li>We do not store or have access to your payment card information</li>
              <li>Payment processing is handled by Apple and Google according to their privacy policies</li>
              <li>We receive only transaction confirmation and subscription status from app stores</li>
              <li>Billing is managed through your app store account</li>
            </ul>
            <p>
              For payment-related questions, please refer to your app store's support resources.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <div className="legal-section-header">
            <Shield size={24} />
            <h2>Data Sharing and Disclosure</h2>
          </div>
          <div className="legal-content">
            <h3>We Do Not Sell Your Data</h3>
            <p>
              Market Sentinel AI does not sell, rent, or trade your personal information to third parties.
            </p>
            
            <h3>Limited Sharing</h3>
            <p>We may share information only in these circumstances:</p>
            <ul>
              <li><strong>Service Providers:</strong> Third-party services that help operate our app (analytics, hosting, customer support)</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Protection:</strong> To protect our rights, safety, or property, or that of our users</li>
            </ul>
            
            <h3>Third-Party Services</h3>
            <p>Our app may use:</p>
            <ul>
              <li>Analytics services (to understand app usage)</li>
              <li>Cloud hosting providers (to store and process data)</li>
              <li>Push notification services</li>
            </ul>
            <p>These services operate under their own privacy policies.</p>
          </div>
        </section>

        <section className="legal-section">
          <h2>Data Retention</h2>
          <div className="legal-content">
            <p>
              We retain your personal information for as long as your account is active or as needed to provide services.
            </p>
            <ul>
              <li>Account data: Retained while your account is active</li>
              <li>Usage analytics: Retained for up to 2 years</li>
              <li>Transaction records: Retained as required by law (typically 7 years)</li>
            </ul>
            <p>
              You may request account deletion at any time. Upon deletion, your data will be removed 
              within 30 days, except where retention is required by law.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <h2>Your Privacy Rights</h2>
          <div className="legal-content">
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Restriction:</strong> Request limitation of data processing</li>
            </ul>
            <p>
              To exercise these rights, contact us through the app or at our support channels.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <h2>International Users</h2>
          <div className="legal-content">
            <p>
              Market Sentinel AI may be accessed from countries around the world. 
              If you are located outside the United States, please note that your information 
              may be transferred to and processed in countries where data protection laws may differ.
            </p>
            <p>
              By using our service, you consent to the transfer of your information to our facilities 
              and service providers as described in this policy.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <h2>Children's Privacy</h2>
          <div className="legal-content">
            <p>
              Market Sentinel AI is not intended for use by individuals under the age of 18. 
              We do not knowingly collect personal information from children. If we discover that 
              a child has provided us with personal information, we will delete it immediately.
            </p>
          </div>
        </section>

        <section className="legal-section">
          <h2>Changes to This Policy</h2>
          <div className="legal-content">
            <p>
              We may update this Privacy Policy periodically. Changes will be posted within the app 
              and on this page with an updated "Last Updated" date. Significant changes will be 
              communicated through app notifications or email.
            </p>
            <p>
              Your continued use of Market Sentinel AI after changes are posted constitutes 
              acceptance of the updated policy.
            </p>
          </div>
        </section>

        <section className="legal-section legal-contact">
          <h2>Contact Us</h2>
          <div className="legal-content">
            <p>
              If you have questions or concerns about this Privacy Policy or our data practices, 
              please contact us through:
            </p>
            <ul>
              <li>In-app support messaging</li>
              <li>Email: support@market-sentinel.net</li>
              <li>Website: https://market-sentinel.net</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPage;
